import { IState } from '../reducers/index';
import * as contextActions from '../actions/contextActions';
import { refreshB2CToken } from '../actions/actionTypes/authActions';
import { B2CToken, EAuthConstants, redirectToLogin } from './authTools';
import { REFRESH_THRESHOLD } from '../constants/global';
import { localStorage } from './storage';

type TokenStore = {
    getState: () => IState;
    dispatch: (action: unknown) => unknown;
};

type JwtExpirationPayload = {
    exp?: number;
};

let inFlightRefresh: Promise<string> | undefined;

const decodeJwtPayload = (token: string): JwtExpirationPayload | undefined => {
    const parts = token.split('.');
    if (parts.length < 2) {
        return undefined;
    }

    try {
        const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
        return JSON.parse(atob(padded)) as JwtExpirationPayload;
    } catch {
        return undefined;
    }
};

export const getJwtExpiration = (token?: string): number | undefined => {
    if (!token) {
        return undefined;
    }

    const exp = decodeJwtPayload(token)?.exp;
    return typeof exp === 'number' ? exp : undefined;
};

export const resolveAccessTokenExpiration = (accessToken?: string, expiresOn?: number): number | undefined => {
    if (typeof expiresOn === 'number' && expiresOn > 0) {
        return expiresOn;
    }

    return getJwtExpiration(accessToken);
};

const persistRefreshedSession = (store: TokenStore, tokenData: B2CToken): string => {
    if (tokenData.id_token) {
        localStorage.setItem(EAuthConstants.IDKey, tokenData.id_token);
    }

    const tokenExpiration = resolveAccessTokenExpiration(tokenData.access_token, tokenData.expires_on);
    store.dispatch(contextActions.setAccessToken(
        tokenData.access_token,
        tokenData.refresh_token,
        false,
        tokenExpiration
    ));

    return tokenData.access_token;
};

const refreshSession = async (store: TokenStore): Promise<string> => {
    const state = store.getState();
    const refreshToken = state.context.refreshToken;
    const applicationConfig = state.config?.appConfig?.applicationConfig;

    if (!refreshToken || !applicationConfig) {
        return state.context.token || '';
    }

    const refreshTokenResponse = await refreshB2CToken(refreshToken, applicationConfig);
    if (refreshTokenResponse.status !== 200) {
        redirectToLogin();
        throw new Error('Token refresh failed');
    }

    const refreshTokenData: B2CToken = await refreshTokenResponse.json();
    if (!refreshTokenData.access_token || !refreshTokenData.refresh_token) {
        redirectToLogin();
        throw new Error('Token refresh returned an incomplete response');
    }

    return persistRefreshedSession(store, refreshTokenData);
};

export const ensureFreshAccessToken = async (store: TokenStore): Promise<string> => {
    const state = store.getState();
    const { token, refreshToken, tokenExpiration } = state.context;
    const applicationConfig = state.config?.appConfig?.applicationConfig;

    if (!refreshToken || !applicationConfig) {
        return token || '';
    }

    const expiration = tokenExpiration || getJwtExpiration(token);
    const now = Math.floor(Date.now() / 1000);
    if (expiration && expiration - now >= REFRESH_THRESHOLD) {
        return token || '';
    }

    if (!inFlightRefresh) {
        inFlightRefresh = refreshSession(store).finally(() => {
            inFlightRefresh = undefined;
        });
    }

    return inFlightRefresh;
};

export const resetTokenRefreshForTests = (): void => {
    inFlightRefresh = undefined;
};
